import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsToDot, faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import * as go from 'gojs';
import { RequirementView } from './calculatorApi';
import { CatalogStructure, hasAssignments, idsOf } from './catalogStructure';

/** Wofür ein Knoten steht — bestimmt Farbe und Verhalten. */
type NodeLayer = 'base' | 'platform' | 'area' | 'subarea' | 'requirement';

interface DiagramNode {
    key: string;
    layer: NodeLayer;
    /** Rahmen, in dem der Knoten steht — GoJS ordnet Mitglieder innerhalb der Gruppe an. */
    group?: string;
    isGroup?: boolean;
    label: string;
    title: string;
    count?: string;
    /** Anforderungs-Id, sofern der Knoten für eine steht. */
    ref?: string;
    warn?: string;
    expandable?: boolean;
    expanded?: boolean;
}

interface DiagramLink {
    key: string;
    from: string;
    to: string;
    /** Wählt die Kantenvorlage — leer für Zugehörigkeit, 'reference' für Optionsverweise. */
    category?: string;
    /** Eine von mehreren Möglichkeiten — gestrichelt, weil sie nicht zwingend ist. */
    alternative: boolean;
    /** Optionsverweis zwischen Anforderungen statt Zugehörigkeit. */
    reference?: boolean;
}

/**
 * Der Katalog als Diagramm.
 *
 * **Warum GoJS und nicht weiter selbst gerechnet.** Das Rendern war nie das Problem — die
 * Anordnung war es. Zweimal lag sie daneben: erst gestapelte Reihen statt Verzweigung, dann ein
 * Baum, dessen Breite die Summe aller Blätter war. Genau dafür gibt es erprobte Verfahren.
 * `LayeredDigraphLayout` ist für gerichtete Graphen mit mehreren Eltern gebaut, und das ist unser
 * Fall wörtlich: ein Bereich hängt an zwei Unterbauten, weil einer von beiden genügt.
 *
 * **Was dadurch dazukommt.** Kanten weichen Knoten aus, statt durch sie hindurchzulaufen. Zoomen
 * und Verschieben sind eingebaut. Und die Anordnung bleibt auch dann tragfähig, wenn der Katalog
 * wächst — das war bei handgerechneten Spalten die Frage der Zeit.
 *
 * **Was es kostet.** GoJS zeichnet auf eine Leinwand, also gibt es hier keine MUI-Komponenten in
 * den Knoten und das Farbschema muss ausdrücklich hineingereicht werden. Für ein Diagramm ist das
 * vertretbar; die übrigen Ansichten bleiben deshalb bei MUI.
 */
export const RequirementDiagram: React.FC<{
    requirements: RequirementView[];
    structure: CatalogStructure | null;
    /** Führt in die Fokusansicht einer Anforderung. */
    onOpen: (id: string) => void;
}> = ({ requirements, structure, onOpen }) => {
    const theme = useTheme();
    const hostRef = useRef<HTMLDivElement>(null);
    const overviewRef = useRef<HTMLDivElement>(null);
    const diagramRef = useRef<go.Diagram | null>(null);
    const overviewDiagram = useRef<go.Overview | null>(null);

    /** Aufgeklappte Bereiche. Alles gleichzeitig zu zeigen war nie lesbar. */
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const toggle = useCallback((key: string) => {
        setExpanded((cur) => {
            const next = new Set(cur);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const model = useMemo(
        () => (structure ? buildModel(requirements, structure, expanded) : null),
        [requirements, structure, expanded],
    );

    // ---- Diagramm einmalig aufbauen ----
    useEffect(() => {
        if (!hostRef.current || diagramRef.current) return undefined;

        const $ = go.GraphObject.make;
        const diagram = $(go.Diagram, hostRef.current, {
            'undoManager.isEnabled': false,
            'toolManager.hoverDelay': 300,
            /**
             * Scrollrad zoomt, statt zu scrollen.
             *
             * GoJS scrollt von Haus aus und zoomt erst mit Strg. Für eine Karte ist das verkehrt
             * herum: gescrollt wird hier fast nie, gezoomt ständig. Strg dreht es um, wer also
             * scrollen will, kann es weiterhin.
             */
            'toolManager.mouseWheelBehavior': go.WheelMode.Zoom,
            // Der Katalog wird gelesen, nicht umgebaut: Verschieben von Knoten würde eine Ordnung
            // suggerieren, die nirgends gespeichert wird.
            allowMove: false,
            allowDelete: false,
            allowInsert: false,
            // Uniform statt UniformToFill: es soll alles zu sehen sein, nicht die Fläche gefüllt.
            initialAutoScale: go.AutoScale.Uniform,
            layout: $(go.LayeredDigraphLayout, {
                direction: 90,
                // Großzügig: die Rahmen der Unterbereiche brauchen sichtbaren Abstand, sonst
                // wirken zwei benachbarte Gruppen wie eine.
                layerSpacing: 48,
                columnSpacing: 30,
                setsPortSpots: false,
                // Mehr Aufwand gegen Kantenkreuzungen: bei rund zweihundert Knoten kostet das
                // Millisekunden und entscheidet darüber, ob man einem Strang folgen kann.
                aggressiveOption: go.LayeredDigraphAggressive.More,
            }),
        });

        diagram.nodeTemplateMap = buildTemplates($, theme, (node) => {
            if (node.expandable) toggle(node.key);
            else if (node.ref) onOpen(node.ref);
        });

        diagram.linkTemplateMap = buildLinkTemplates($, theme);
        diagram.groupTemplate = buildGroupTemplate($, theme);

        // Beim Wählen eines Knotens treten seine Verweise hervor — und nur seine.
        diagram.addDiagramListener('ChangedSelection', (e) => {
            e.diagram.clearHighlighteds();
            const node = e.diagram.selection.first();
            if (!(node instanceof go.Node)) return;
            node.findLinksConnected().each((link) => {
                if (link.data?.reference) link.isHighlighted = true;
            });
        });

        // Punktraster: es gibt dem Bild Tiefe und macht beim Verschieben sichtbar, dass man sich
        // bewegt — ohne Bezugspunkt wirkt eine leere Fläche wie ein Standbild.
        diagram.grid = $(go.Panel, 'Grid',
            { gridCellSize: new go.Size(18, 18) },
            $(go.Shape, 'LineH', { strokeWidth: 0 }),
            $(go.Shape, 'LineV', { strokeWidth: 0 }),
        );

        diagramRef.current = diagram;

        if (overviewRef.current) {
            overviewDiagram.current = $(go.Overview, overviewRef.current, {
                observed: diagram,
                contentAlignment: go.Spot.Center,
            });
        }

        return () => {
            if (overviewDiagram.current) {
                overviewDiagram.current.div = null;
                overviewDiagram.current = null;
            }
            diagram.div = null;
            diagramRef.current = null;
        };
        // Absichtlich einmalig: Vorlagen werden bei Themenwechsel eigens erneuert.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- Farbschema nachziehen ----
    useEffect(() => {
        const diagram = diagramRef.current;
        if (!diagram) return;
        const $ = go.GraphObject.make;
        diagram.nodeTemplateMap = buildTemplates($, theme, (node) => {
            if (node.expandable) toggle(node.key);
            else if (node.ref) onOpen(node.ref);
        });
        diagram.linkTemplateMap = buildLinkTemplates($, theme);
        diagram.groupTemplate = buildGroupTemplate($, theme);
        diagram.div!.style.backgroundColor = theme.palette.background.default;

        const dotted = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)';
        diagram.grid.elements.each((el) => {
            const shape = el as go.Shape;
            shape.stroke = dotted;
            shape.strokeWidth = 1;
            shape.strokeDashArray = [1, 17];
        });
    }, [theme, toggle, onOpen]);

    // ---- Daten einspielen ----
    useEffect(() => {
        const diagram = diagramRef.current;
        if (!diagram || !model) return;

        diagram.model = new go.GraphLinksModel({
            nodeDataArray: model.nodes,
            linkDataArray: model.links,
            linkKeyProperty: 'key',
            nodeGroupKeyProperty: 'group',
            nodeIsGroupProperty: 'isGroup',
            // Die Art des Knotens wählt seine Vorlage — GoJS liest dafür 'category'.
            nodeCategoryProperty: 'layer',
            linkCategoryProperty: 'category',
        });

        // Nach dem Aufklappen ist das Bild ein anderes; der alte Ausschnitt passt dann nicht mehr.
        diagram.addDiagramListener('InitialLayoutCompleted', () => diagram.zoomToFit());
    }, [model]);

    const zoom = (factor: number) => {
        const diagram = diagramRef.current;
        if (diagram) diagram.commandHandler.increaseZoom(factor);
    };

    if (!structure) return <Alert severity="info">Aufbau wird geladen …</Alert>;

    if (!hasAssignments(structure)) {
        return (
            <Alert severity="warning">
                Der Aufbau kommt ohne Zuordnung der Anforderungen — vermutlich läuft noch eine
                ältere Server-Version. Nach einem Neustart des Kalkulator-Dienstes füllt sich das
                Diagramm.
            </Alert>
        );
    }

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {model?.nodes.length ?? 0} Knoten · {model?.links.length ?? 0} Verbindungen
                </Typography>

                <Button size="small" onClick={() => zoom(1.2)} startIcon={<FontAwesomeIcon icon={faPlus} />}>
                    Größer
                </Button>
                <Button size="small" onClick={() => zoom(1 / 1.2)} startIcon={<FontAwesomeIcon icon={faMinus} />}>
                    Kleiner
                </Button>
                <Button
                    size="small"
                    onClick={() => diagramRef.current?.zoomToFit()}
                    startIcon={<FontAwesomeIcon icon={faArrowsToDot} />}
                >
                    Einpassen
                </Button>
                {expanded.size > 0 && (
                    <Button size="small" onClick={() => setExpanded(new Set())}>
                        Alle zuklappen
                    </Button>
                )}

                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Scrollrad zoomt · ziehen verschiebt · Klick auf einen Bereich klappt ihn auf,
                    auf eine Anforderung öffnet sie und zeigt ihre Verweise
                </Typography>
            </Stack>

            <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
                <Box ref={hostRef} sx={{ height: '72vh', minHeight: 480, width: '100%' }} />

                {/*
                  * Die Übersichtskarte beantwortet die Frage, die bei jedem zoombaren Bild als
                  * Erstes aufkommt: wo bin ich gerade, und wie viel liegt außerhalb?
                  */}
                <Box
                    ref={overviewRef}
                    sx={{
                        position: 'absolute', right: 10, bottom: 10,
                        width: 190, height: 130,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        overflow: 'hidden',
                    }}
                />
            </Paper>
        </Stack>
    );
};

/** Die Farbe eines Knotens — dieselbe Bedeutung wie in den übrigen Ansichten. */
function colorOf(layer: NodeLayer, theme: Theme): string {
    switch (layer) {
        case 'base': return theme.palette.success.main;
        case 'platform': return theme.palette.warning.main;
        case 'area': return theme.palette.primary.main;
        case 'subarea': return theme.palette.info.main;
        default: return theme.palette.text.secondary;
    }
}

/**
 * Die Knotenvorlagen, eine je Art.
 *
 * **Warum getrennt und nicht eine mit Bedingungen.** Der erste Versuch war eine Vorlage, in der
 * Zahl, Hinweiszeichen und Trennlinie über `visible`-Bindungen ein- und ausgeblendet wurden. Das
 * ging schief: auf jedem Knoten klebten am Ende ein Warnzeichen und eine leere Pille, weil eine
 * nicht gesetzte Eigenschaft nicht dasselbe ist wie eine auf falsch gesetzte. Getrennte Vorlagen
 * enthalten nur, was ihre Art wirklich hat — dann gibt es nichts zu verstecken.
 *
 * **Warum so ruhig.** Ein Diagramm wird nicht dadurch lesbar, dass jeder Knoten mehr zeigt, sondern
 * dadurch, dass alle gleich aussehen und nur das Wesentliche tragen: eine Kennung, einen Namen,
 * eine Farbe für die Art. Alles Weitere steht im Tooltip oder in der Fokusansicht.
 */
function buildTemplates(
    $: typeof go.GraphObject.make,
    theme: Theme,
    onClick: (node: DiagramNode) => void,
): go.Map<string, go.Node> {
    const dark = theme.palette.mode === 'dark';

    /** Gemeinsame Grundlage: Karte mit farbiger Kante links, Kennung, Name. */
    const card = (layer: NodeLayer, opts: { wide?: boolean; bold?: boolean } = {}) => {
        const accent = colorOf(layer, theme);
        const width = opts.wide ? 232 : 206;

        // 'Spot' und nicht 'Auto': nur so lässt sich der Aufklapp-Knopf später an den unteren Rand
        // legen. In einem 'Auto'-Panel läge er im Inneren der Karte.
        return $(go.Node, 'Spot',
            {
                cursor: 'pointer',
                isShadowed: true,
                shadowBlur: dark ? 5 : 8,
                shadowColor: dark ? 'rgba(0,0,0,0.5)' : 'rgba(15,23,42,0.13)',
                shadowOffset: new go.Point(0, 1),
                fromSpot: go.Spot.Bottom,
                toSpot: go.Spot.Top,
                click: (_e: go.InputEvent, obj: go.GraphObject) => {
                    const data = (obj.part as go.Node)?.data as DiagramNode | undefined;
                    if (data) onClick(data);
                },
                mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
                    const shape = (obj as go.Node).findObject('BODY') as go.Shape | null;
                    if (shape) shape.stroke = accent;
                },
                mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
                    const shape = (obj as go.Node).findObject('BODY') as go.Shape | null;
                    if (shape) shape.stroke = theme.palette.divider;
                },
                toolTip: $<go.Adornment>('ToolTip',
                    $(go.TextBlock,
                        { margin: 8, maxSize: new go.Size(300, NaN), font: '12px system-ui, sans-serif' },
                        new go.Binding('text', '', (d: DiagramNode) =>
                            [d.label, d.title, d.warn].filter(Boolean).join('\n'))),
                ),
            },

            $(go.Panel, 'Auto',
                $(go.Shape, 'RoundedRectangle',
                    {
                        name: 'BODY',
                        parameter1: 6,
                        strokeWidth: 1,
                        stroke: theme.palette.divider,
                        fill: theme.palette.background.paper,
                    }),

                $(go.Panel, 'Horizontal', { defaultAlignment: go.Spot.Top },
                // Die farbige Kante trägt die Art des Knotens, ohne Platz für Text zu kosten.
                $(go.Shape, 'Rectangle', {
                    width: 3,
                    stretch: go.GraphObject.Vertical,
                    strokeWidth: 0,
                    fill: accent,
                }),
                $(go.Panel, 'Vertical',
                    {
                        defaultAlignment: go.Spot.Left,
                        margin: new go.Margin(7, 10, 7, 9),
                        width,
                    },
                    $(go.TextBlock,
                        {
                            font: `${opts.bold ? 600 : 500} 11.5px ui-monospace, SFMono-Regular, Menlo, monospace`,
                            stroke: accent,
                            alignment: go.Spot.Left,
                            maxSize: new go.Size(width, 15),
                            overflow: go.TextOverflow.Ellipsis,
                        },
                        new go.Binding('text', 'label'),
                    ),
                    $(go.TextBlock,
                        {
                            font: '11px system-ui, sans-serif',
                            stroke: theme.palette.text.secondary,
                            alignment: go.Spot.Left,
                            margin: new go.Margin(2, 0, 0, 0),
                            maxSize: new go.Size(width, 30),
                            overflow: go.TextOverflow.Ellipsis,
                        },
                        new go.Binding('text', 'title'),
                    ),
                    ),
                ),
            ),
        ) as unknown as go.Node;
    };

    /** Aufklappbare Knoten bekommen unten einen Knopf — dort entsteht die Verzweigung. */
    const withToggle = (node: go.Node, layer: NodeLayer) => {
        const accent = colorOf(layer, theme);
        node.add($(go.Panel, 'Auto',
            { alignment: go.Spot.Bottom, alignmentFocus: go.Spot.Center, cursor: 'pointer' },
            $(go.Shape, 'Circle', {
                width: 14, height: 14, strokeWidth: 1, stroke: accent,
                fill: theme.palette.background.paper,
            }),
            $(go.TextBlock,
                { font: '600 10px system-ui, sans-serif', stroke: accent },
                new go.Binding('text', 'expanded', (e: boolean) => (e ? '−' : '+'))),
        ) as unknown as go.GraphObject);
        return node;
    };

    const map = new go.Map<string, go.Node>();
    map.add('base', card('base', { bold: true }));
    map.add('platform', withToggle(card('platform', { wide: true }), 'platform'));
    map.add('area', withToggle(card('area', { wide: true, bold: true }), 'area'));
    map.add('subarea', card('subarea'));
    map.add('requirement', card('requirement'));
    map.add('', card('requirement'));
    return map;
}

/**
 * Der Rahmen um die Anforderungen eines Unterbereichs.
 *
 * Ohne ihn legte die Anordnung alle Anforderungen einer Ebene nebeneinander — bei siebzehn
 * Sprechstellen quer über den Bildschirm. Im Rahmen stehen sie untereinander, und die äußere
 * Anordnung schiebt nur noch Rahmen statt einzelner Kästen.
 */
function buildGroupTemplate($: typeof go.GraphObject.make, theme: Theme): go.Group {
    return $(go.Group, 'Auto',
        {
            selectable: false,
            layout: $(go.GridLayout, {
                wrappingColumn: 1,
                spacing: new go.Size(0, 7),
                alignment: go.GridAlignment.Position,
                cellSize: new go.Size(1, 1),
            }),
            fromSpot: go.Spot.Bottom,
            toSpot: go.Spot.Top,
        },
        $(go.Shape, 'RoundedRectangle', {
            parameter1: 8,
            strokeWidth: 1,
            stroke: theme.palette.divider,
            fill: 'transparent',
        }),
        $(go.Panel, 'Vertical', { margin: 10, defaultAlignment: go.Spot.Left },
            $(go.TextBlock,
                {
                    font: '600 10px system-ui, sans-serif',
                    stroke: theme.palette.text.secondary,
                    margin: new go.Margin(0, 0, 7, 1),
                },
                new go.Binding('text', 'label')),
            $(go.Placeholder),
        ),
    ) as unknown as go.Group;
}

/** Die Kantenvorlagen: Zugehörigkeit immer sichtbar, Verweise nur auf Anfrage. */
function buildLinkTemplates($: typeof go.GraphObject.make, theme: Theme): go.Map<string, go.Link> {
    const structural = $(go.Link,
        {
            routing: go.Routing.AvoidsNodes,
            corner: 8,
            curve: go.Curve.JumpOver,
            toShortLength: 5,
            fromSpot: go.Spot.Bottom,
            toSpot: go.Spot.Top,
        },
        $(go.Shape,
            { strokeWidth: 1.3, stroke: theme.palette.text.disabled },
            new go.Binding('strokeDashArray', 'alternative', (a: boolean) => (a ? [5, 4] : null)),
        ),
        $(go.Shape,
            { toArrow: 'Standard', strokeWidth: 0, scale: 0.7, fill: theme.palette.text.disabled }),
    );

    /**
     * Optionsverweise sind unsichtbar, bis ein Knoten gewählt ist.
     *
     * Hundertsechsundsechzig Verweise gleichzeitig sind keine Information, sondern ein Gitter über
     * dem Bild — sie liefen quer durch die Karten und machten die Struktur unlesbar. Sichtbar sind
     * sie genau dann, wenn jemand nach ihnen fragt, indem er einen Knoten anklickt.
     */
    const reference = $(go.Link,
        {
            routing: go.Routing.AvoidsNodes,
            corner: 8,
            curve: go.Curve.JumpOver,
            toShortLength: 5,
            visible: false,
            // Nicht mitgerechnet: sonst zöge jeder Verweis zwei Anforderungen zueinander und die
            // Ebenen verlören ihre Ordnung.
            isLayoutPositioned: false,
        },
        new go.Binding('visible', 'isHighlighted').ofObject(),
        $(go.Shape,
            { strokeWidth: 1.6, stroke: theme.palette.info.main, strokeDashArray: [5, 4] }),
        $(go.Shape,
            { toArrow: 'Standard', strokeWidth: 0, scale: 0.75, fill: theme.palette.info.main }),
    );

    const map = new go.Map<string, go.Link>();
    map.add('', structural as unknown as go.Link);
    map.add('reference', reference as unknown as go.Link);
    return map;
}

/**
 * Baut Knoten und Kanten.
 *
 * Die Anordnung überlässt diese Funktion vollständig GoJS — hier steht nur, was womit
 * zusammenhängt. Genau diese Trennung hat vorher gefehlt: Struktur und Anordnung in einer Funktion
 * zu rechnen war der Grund, warum jede Änderung an der einen die andere kaputt machte.
 */
function buildModel(
    requirements: RequirementView[],
    structure: CatalogStructure,
    expanded: Set<string>,
) {
    const byId = new Map(requirements.map((r) => [r.id, r]));
    const groupByPath = new Map(structure.groups.map((g) => [g.path, g]));
    const nodes: DiagramNode[] = [];
    const links: DiagramLink[] = [];
    const present = new Set<string>();

    const add = (n: DiagramNode) => {
        nodes.push(n);
        present.add(n.key);
    };

    const idsOfArea = (path: string): string[] =>
        structure.groups
            .filter((g) => g.path === path || g.path.startsWith(`${path}.`))
            .flatMap(idsOf);

    // ---- Grundlage ----
    const konfiguration = groupByPath.get('Konfiguration');
    const baseReqs = (konfiguration ? idsOf(konfiguration) : [])
        .map((id) => byId.get(id))
        .filter((r): r is RequirementView => !!r && !r.implicit);

    for (const r of baseReqs) {
        add({ key: `req:${r.id}`, layer: 'base', label: r.id, title: r.titel || '', ref: r.id });
    }

    const baseKey = baseReqs.length > 0 ? `req:${baseReqs[0].id}` : null;

    // ---- Unterbau ----
    for (const p of structure.platforms) {
        const key = `plat:${p.id}`;
        add({
            key, layer: 'platform', label: p.title,
            title: p.fulfilledBy.join(' oder '),
        });
        if (baseKey) links.push({ key: `l-${baseKey}-${key}`, from: baseKey, to: key, alternative: false });
    }

    const unknownKey = 'plat:offen';
    let unknownUsed = false;

    // ---- Bereiche ----
    const areas = structure.groups
        .filter((g) => !g.path.includes('.') && g.path !== 'Konfiguration')
        .map((g) => ({ ...g, count: idsOfArea(g.path).length }))
        .sort((a, b) => b.count - a.count);

    for (const a of areas) {
        const key = `area:${a.path}`;
        const open = expanded.has(key);

        add({
            key, layer: 'area', label: a.title,
            title: `${a.count} Anforderungen`,
            expandable: a.count > 0,
            expanded: open,
            warn: a.limitedWithout
                ? `Läuft ohne ${a.limitedWithout} — dann mit Funktionseinschränkungen.`
                : a.requiredPlatforms.length === 0
                    ? 'Voraussetzung nicht hinterlegt — das heißt nicht, dass der Bereich ohne Unterbau läuft.'
                    : undefined,
        });

        if (a.requiredPlatforms.length === 0) {
            unknownUsed = true;
            links.push({ key: `l-${unknownKey}-${key}`, from: unknownKey, to: key, alternative: true });
        }

        // Jede Möglichkeit bekommt ihre Kante. Dass Funk zwei hat, ist die Aussage.
        const alternative = a.requiredPlatforms.length > 1;
        for (const pid of a.requiredPlatforms) {
            links.push({ key: `l-${pid}-${key}`, from: `plat:${pid}`, to: key, alternative });
        }

        if (!open) continue;

        // ---- Unterbereiche und Anforderungen ----
        const subPaths = structure.groups
            .filter((g) => (g.path === a.path || g.path.startsWith(`${a.path}.`)) && idsOf(g).length > 0)
            .map((g) => g.path)
            .sort();

        for (const sub of subPaths) {
            const group = groupByPath.get(sub);
            const list = (group ? idsOf(group) : [])
                .map((id) => byId.get(id))
                .filter((r): r is RequirementView => !!r)
                .sort((x, z) => x.id.localeCompare(z.id, undefined, { numeric: true }));

            // Anforderungen des Bereichs selbst hängen direkt am Bereich — ein Zwischenknoten
            // „direkt" wäre ein Kasten, der nichts benennt.
            // Jeder Unterbereich bekommt einen Rahmen; die Anforderungen stehen darin statt in
            // einer Reihe. Bei siebzehn Sprechstellen ist das der Unterschied zwischen einem Bild
            // und einem Bildschirmbreiten-Band.
            const groupKey = `grp:${sub}`;
            add({
                key: groupKey, layer: 'subarea', isGroup: true,
                label: sub === a.path ? a.title : sub.slice(a.path.length + 1),
                title: `${list.length}`,
            });
            links.push({ key: `l-${key}-${groupKey}`, from: key, to: groupKey, alternative: false });

            for (const r of list) {
                const rk = `req:${r.id}`;
                if (!present.has(rk)) {
                    add({
                        key: rk, layer: 'requirement', label: r.id, title: r.titel || '',
                        ref: r.id, group: groupKey,
                    });
                }
            }
        }
    }

    if (unknownUsed) {
        add({
            key: unknownKey, layer: 'platform', label: 'Voraussetzung offen',
            title: 'noch nicht hinterlegt',
            warn: 'Für diese Bereiche ist nicht hinterlegt, worauf sie aufsetzen.',
        });
        if (baseKey) {
            links.push({ key: `l-${baseKey}-${unknownKey}`, from: baseKey, to: unknownKey, alternative: true });
        }
    }

    // ---- Optionsverweise zwischen sichtbaren Anforderungen ----
    for (const r of requirements) {
        const from = `req:${r.id}`;
        if (!present.has(from)) continue;

        for (const g of r.optionGroups ?? []) {
            for (const o of g.options ?? []) {
                const to = o.anforderungId ? `req:${o.anforderungId}` : null;
                if (!to || !present.has(to) || to === from) continue;
                links.push({
                    key: `o-${from}-${to}`,
                    from, to,
                    category: 'reference',
                    alternative: !g.mandatory,
                    reference: true,
                });
            }
        }
    }

    return { nodes, links };
}

export default RequirementDiagram;
